import {
  type DocumentNode,
  type OperationVariables,
  useQuery
} from '@apollo/client';

import type { QualifyingWorkspace } from '@/components/userDashboard/workflowsCards/workspaceFilterMatch';
import { env } from '@/constant/env';

type UseWorkspaceFromQueryArgs<TData, TVariables extends OperationVariables> = {
  query: DocumentNode;
  // Route segment (workflow/group/trace id). Empty string means "not this kind
  // of route", which skips the query.
  id: string;
  variables: TVariables;
  // Pull the workspace out of the (possibly partial) query result.
  select: (data: TData | undefined) => QualifyingWorkspace | null;
};

export type WorkspaceLookup = {
  workspace: QualifyingWorkspace | null;
  loading: boolean;
  error: boolean;
};

// Shared implementation behind useWorkspaceForWorkflow/Group/Trace: gates the
// query on the feature flag + a present id, reads it `cache-first`, and exposes
// an `error` flag so callers can avoid showing a misleading state (e.g. a
// "Production" badge) when the lookup actually failed rather than resolving to
// "no workspace".
export const useWorkspaceFromQuery = <
  TData,
  TVariables extends OperationVariables
>({
  query,
  id,
  variables,
  select
}: UseWorkspaceFromQueryArgs<TData, TVariables>): WorkspaceLookup => {
  const enabled = env.FEATURE_FLAGS.USE_WORKFLOW_CARDS && Boolean(id);
  const { data, loading, error } = useQuery<TData, TVariables>(query, {
    variables,
    skip: !enabled,
    fetchPolicy: 'cache-first'
  });
  return {
    workspace: enabled ? select(data) : null,
    loading,
    error: Boolean(error)
  };
};
